import {
  CITY_TITLE,
  COMPANY_TITLE,
  INVERTER_POWER_THRESHOLD_W,
  INVERTER_REDUCTION_START_DATE,
  NON_ELIGIBLE_COMPENSATION_TYPES_FOR_INVERTER_REDUCTION,
} from './constants';
import i18n from '../plugins/i18n';
import i18nData from '../assets/i18n/de.json';
import { transformI18nDataToLabelsObject } from '@newnetzportal/netzportal-webapp-storybook-components';

export const deleteAllEmptyFields = (objectData) => {
  Object.keys(objectData).forEach((el) => {
    if (
      objectData[el] === undefined ||
      objectData[el] === null ||
      objectData[el] === '' ||
      (typeof objectData[el] === 'object' &&
        Object.keys(objectData[el]).length === 0) // Check for empty objects
    ) {
      delete objectData[el];
    } else if (typeof objectData[el] === 'object' && objectData[el] !== null) {
      // Recursively apply the deleteAllEmptyFields function for nested objects
      objectData[el] = deleteAllEmptyFields(objectData[el]);
      if (Object.keys(objectData[el]).length === 0) {
        delete objectData[el];
      }
    }
  });
  return objectData;
};

export const removeNullFromArray = (dataArray) => {
  return dataArray.filter((item) => item !== null);
};

export const deleteAllEmptyFieldsIncludingEmptyArrayValues = (objectData) => {
  Object.keys(objectData).forEach((el) => {
    if (
      objectData[el] === undefined ||
      objectData[el] === null ||
      objectData[el] === '' ||
      (typeof objectData[el] === 'object' &&
        Object.keys(objectData[el]).length === 0) // Check for empty objects
    ) {
      delete objectData[el];
    } else if (typeof objectData[el] === 'object' && objectData[el] !== null) {
      // Recursively apply the deleteAllEmptyFields function for nested objects
      objectData[el] = deleteAllEmptyFields(objectData[el]);
      if (Array.isArray(objectData[el])) {
        // If the property is an array and its key is 'counter_id_data', remove null values
        objectData[el] = removeNullFromArray(objectData[el]);
      }
      if (Object.keys(objectData[el]).length === 0) {
        delete objectData[el];
      }
    }
  });
  return objectData;
};

export const areAllPropsNullUndefinedOrBlank = (obj) => {
  for (const key in obj) {
    const value = obj[key];
    if (
      value !== null &&
      value !== undefined &&
      !(typeof value === 'string' && value.trim() === '')
    ) {
      return false;
    }
  }
  return true;
};

export const deletePersonalParametersBasedOnTitle = (objectData) => {
  if (objectData.title === COMPANY_TITLE) {
    delete objectData.name;
    delete objectData.surname;
    delete objectData.city_name;
  } else if (objectData.title === CITY_TITLE) {
    delete objectData.name;
    delete objectData.surname;
    delete objectData.company_name;
  } else {
    delete objectData.company_name;
    delete objectData.city_name;
  }
  return objectData;
};

export function germanNumberFormat(value) {
  if (typeof value !== 'number') {
    return value;
  }
  return value.toLocaleString('de-DE');
}

// Accepts Date objects or 'DD.MM.YYYY' strings and returns a Date or null
export function parseDate(dateInput) {
  if (!dateInput) {
    return null;
  }
  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput;
  }
  if (typeof dateInput === 'string' && dateInput.includes('.')) {
    const [day, month, year] = dateInput.split('.');
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(dateInput);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export const removeAttributes = (obj, attributesToRemove) => {
  if (!obj || typeof obj !== 'object') return obj;

  const newObj = { ...obj };

  attributesToRemove.forEach((attr) => {
    // eslint-disable-next-line no-prototype-builtins
    if (newObj.hasOwnProperty(attr)) {
      delete newObj[attr];
    }
  });

  return newObj;
};

export const getTranslations = (translationValue) => {
  return {
    ...transformI18nDataToLabelsObject(
      i18nData,
      translationValue,
      i18n.t.bind(i18n)
    ),
  };
};

export const removeUUIDFromFileName = (fileNameWithUUID) => {
  if (!fileNameWithUUID) {
    return '';
  }
  return fileNameWithUUID.split('/').pop();
};

/**
 * Handles dynamic checks for whether inverter reduction warning should show.
 * @param {Object} params
 * @param {Object} params.pvOrder - The previous values object (e.g. selectedPhotovoltaic)
 * @param {string} params.valueToCheck - Field name to check
 * @param {any} params.newValue - New/current value of the field
 */
export function shouldShowInverterReductionWarning({
  pvOrder,
  valueToCheck,
  newValue,
}) {
  const {
    pv_system_infos,
    permissions,
    implementing_date,
    feed_in_compensation,
  } = pvOrder || {};

  const selected = permissions?.inverter_reduction_selected || false;
  const prevPower = pv_system_infos?.pv_system_power_W || 0;

  // Determine current values based on valueToCheck
  const currentPower =
    valueToCheck === 'pvSystemPower' ? Number(newValue) : prevPower;
  const currentDate =
    valueToCheck === 'implementingDate'
      ? new Date(newValue)
      : new Date(implementing_date);
  const currentCompensation =
    valueToCheck === 'feedInCompensation' ? newValue : feed_in_compensation;

  // Check all Inverter reduction requirements
  const isPowerValid = currentPower < INVERTER_POWER_THRESHOLD_W;
  const isDateValid = currentDate >= new Date(INVERTER_REDUCTION_START_DATE);
  const isCompensationValid =
    !NON_ELIGIBLE_COMPENSATION_TYPES_FOR_INVERTER_REDUCTION.includes(
      currentCompensation
    );

  const fulfilledRequirements = [
    isPowerValid,
    isDateValid,
    isCompensationValid,
  ];
  const numberOfFulfilled = fulfilledRequirements.filter(Boolean).length;
  const numberOfBroken = fulfilledRequirements.length - numberOfFulfilled;

  // Map valueToCheck to index
  const indexMap = {
    pvSystemPower: 0,
    implementingDate: 1,
    feedInCompensation: 2,
  };
  const changedIndex = indexMap[valueToCheck];

  if (selected) {
    // Checkbox selected: show warning only if exactly one requirement is broken
    // AND that broken requirement corresponds to the changed field
    if (numberOfBroken === 1 && fulfilledRequirements[changedIndex] === false) {
      return true;
    }
    return false;
  } else {
    // Checkbox not selected: show warning if all requirements are fulfilled
    return numberOfFulfilled === fulfilledRequirements.length;
  }
}
